import Icons from "../Icons";

const PrivacyContent = () => {
  return (
    <div className="flex flex-col text-[#B4C1E4] gap-y-4">
      <p>
        <strong>THINCCC PRIVACY POLICY</strong>
        <br />
        Effective Date: FEBRUARY 14, 2025
      </p>

      <p>
        <strong>PLEASE READ THIS PRIVACY POLICY CAREFULLY.</strong> BY ACCESSING
        OR USING THE THINCCC PLATFORM (THE “PLATFORM”), YOU AGREE TO THE TERMS
        DESCRIBED HEREIN AND ALL TERMS INCORPORATED BY REFERENCE. IF YOU DO NOT
        AGREE, YOU MUST NOT USE THE PLATFORM.
      </p>

      <p>
        <strong>1. INTRODUCTION AND SCOPE</strong>
      </p>
      <p>
        <strong>1.1 Who We Are</strong>
        <br />
        This Privacy Policy (“Policy”) is issued by thinccc, Inc. (“thinccc,”
        “we,” “our,” or “us”), a Delaware corporation. thinccc provides a
        challenge-based learning Platform for high schools and other educational
        organizations, focusing on developing and assessing skills such as
        communication, collaboration, and critical thinking.
      </p>

      <p>
        <strong>1.2 Who This Policy Applies To</strong>
      </p>
      <ul>
        <li>
          <strong>Students (13+):</strong> High school students who use thinccc
          under the direction of their school.
        </li>
        <li>
          <strong>Educators and School Administrators:</strong> Teachers, school
          staff, and authorized personnel with administrative privileges.
        </li>
        <li>
          <strong>Other Authorized Users:</strong> Anyone else authorized by the
          Educational Institution to access thinccc (e.g., mentors, designated
          corporate sponsors in a limited capacity).
        </li>
      </ul>

      <p>
        <strong>1.3 Age Requirements</strong>
        <br />
        No Users Under 13: The thinccc Platform is intended for use by
        individuals age 13 or older. We do not knowingly collect personal
        information from children under 13. If you are under 13, please do not
        attempt to register for or use the Platform.
      </p>

      <p>
        <strong>1.4 Key Definitions</strong>
      </p>
      <ul>
        <li>
          <strong>“Educational Institution”:</strong> The school, district, or
          organization that grants students access to thinccc and with which we
          have a contractual relationship.
        </li>
        <li>
          <strong>“Student Data”:</strong> Any information that identifies or
          could reasonably be used to identify a student, including information
          directly provided by the student or the Educational Institution.
        </li>
        <li>
          <strong>“Aggregated Data”:</strong> Data that has been stripped of
          personal identifiers and combined in a way that cannot be used to
          identify an individual.
        </li>
        <li>
          <strong>“Services”:</strong> All services provided by thinccc,
          including the Platform’s challenge-based learning environment,
          analytics, and assessments.
        </li>
      </ul>

      <p>
        <strong>2. INFORMATION WE COLLECT</strong>
      </p>
      <p>
        We collect limited personal information and user engagement data to
        provide and improve our Services, support educational outcomes, and
        offer appropriate feedback to participating schools and students. Below
        is a general overview of categories of information we may collect:
      </p>

      <ul>
        <li>
          <strong>Basic Account Details</strong>
          <ul>
            <li>
              Name, school-provided email address, and associated school or
              district.
            </li>
            <li>
              Grade level or other academic affiliation necessary for
              credentialing and account management.
            </li>
          </ul>
        </li>
        <li>
          <strong>Engagement & Interaction Data</strong>
          <ul>
            <li>
              Logs of when users view program materials or interact with various
              resources (e.g., reading briefs, checking milestones).
            </li>
            <li>Activity timestamps and session durations.</li>
            <li>Platform interaction metrics to gauge overall engagement.</li>
          </ul>
        </li>
        <li>
          <strong>User-Generated Content</strong>
          <ul>
            <li>Reflections & responses submitted for tasks or assessments.</li>
            <li>
              Optional media uploads such as files, audio, or video clips.
            </li>
            <li>Team & peer feedback focused on collaboration and critique.</li>
          </ul>
        </li>
        <li>
          <strong>Assessment & Performance Indicators</strong>
          <ul>
            <li>
              Quiz scores or completion status, timing metrics, and submission
              outcomes.
            </li>
            <li>Progress reports on growth or milestone achievements.</li>
          </ul>
        </li>
        <li>
          <strong>Technical & Analytics Data</strong>
          <ul>
            <li>
              IP addresses, browser types, and session data for security and
              optimization.
            </li>
            <li>Communication events (timestamps only).</li>
          </ul>
        </li>
      </ul>

      <p>
        <strong>3. HOW WE USE THE INFORMATION</strong>
      </p>
      <ul>
        <li>
          <strong>Educational Purposes</strong>
          <ul>
            <li>
              Delivering, evaluating, and improving the learning experience.
            </li>
            <li>Quantifying and tracking student skill development.</li>
            <li>Analyzing usage patterns to enhance functionality.</li>
          </ul>
        </li>
        <li>
          <strong>Aggregated Insights</strong>
          <br />
          Aggregated data is used for research, reports, and product
          development, without identifying individuals.
        </li>
        <li>
          <strong>Prohibited Uses</strong>
          <ul>
            <li>No selling of Student Data.</li>
            <li>No targeted ads using Student Data.</li>
            <li>No unauthorized profiling.</li>
          </ul>
        </li>
      </ul>

      <p>
        <strong>4. DISCLOSURE AND SHARING OF INFORMATION</strong>
      </p>
      <ul>
        <li>
          <strong>With the Educational Institution:</strong> Shared with
          authorized teachers, administrators, and staff for legitimate
          purposes.
        </li>
        <li>
          <strong>With Third-Party Service Providers:</strong> Data is processed
          by providers under strict confidentiality.
        </li>
        <li>
          <strong>With Corporate Sponsors:</strong> Only Aggregated or
          de-identified data is shared.
        </li>
        <li>
          <strong>Legal Compliance and Safety:</strong> Data may be disclosed if
          required by law or for safety reasons.
        </li>
      </ul>

      <p>
        <strong>5. DATA SECURITY</strong>
      </p>
      <ul>
        <li>
          <strong>Security Measures:</strong> Encryption, role-based access, and
          routine security testing are employed.
        </li>
        <li>
          <strong>Incident Response:</strong> Breach notifications will be
          promptly issued to the Educational Institution with details and
          corrective actions.
        </li>
      </ul>

      <p>
        <strong>6. DATA RETENTION AND DELETION</strong>
      </p>
      <ul>
        <li>
          <strong>Retention Period:</strong> Data is retained only as long as
          necessary to provide Services or comply with obligations.
        </li>
        <li>
          <strong>Deletion Requests:</strong> Data will be securely deleted upon
          a valid request unless legally required otherwise.
        </li>
      </ul>

      <p>
        <strong>7. FERPA COMPLIANCE</strong>
      </p>
      <ul>
        <li>
          <strong>School Official:</strong> thinccc acts as a “school official”
          under FERPA, using Student Data for educational purposes only.
        </li>
        <li>
          <strong>Control:</strong> The Educational Institution determines
          access to Student Data.
        </li>
      </ul>

      <p>
        <strong>8. USER RIGHTS AND ACCESS</strong>
      </p>
      <ul>
        <li>
          <strong>Educational Institutions:</strong> Schools may access, update,
          or delete Student Data.
        </li>
        <li>
          <strong>Students and Parents/Guardians:</strong> Requests should be
          made through the Educational Institution.
        </li>
      </ul>

      <p>
        <strong>9. INTERNATIONAL USERS</strong>
        <br />
        Data collected is subject to U.S. laws, and users consent to data
        transfers by using the Platform.
      </p>

      <p>
        <strong>10. CHANGES TO THIS POLICY</strong>
      </p>
      <ul>
        <li>
          <strong>Updates:</strong> Material changes will be communicated to the
          Educational Institution at least 30 days in advance.
        </li>
        <li>
          <strong>Continued Use:</strong> Using the Platform after changes
          indicates acceptance.
        </li>
      </ul>

      <p>
        <strong>11. CONTACT INFORMATION</strong>
        <br />
        For privacy-related questions, contact: privacy@thinccc.com.
      </p>

      <p>
        <strong>12. GOVERNING LAW AND DISPUTES</strong>
        <br />
        This Policy is governed by the laws of the state of [State]. Disputes
        are resolved per the Terms of Service and applicable law.
      </p>

      <p>
        <strong>13. SEVERABILITY</strong>
        <br />
        If any provision is invalid, the remaining provisions remain in full
        force and effect.
      </p>

      <p>
        <strong>14. ACKNOWLEDGMENT AND ACCEPTANCE</strong>
        <br />
        By accessing the Platform, you agree to this Policy. If you do not
        agree, discontinue use of the Platform.
      </p>

      <p>Last Updated: FEBRUARY 14, 2025</p>
    </div>
  );
};

const Privacy = ({ isOpen, setIsOpen }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-darkblue bg-opacity-65 flex items-center justify-center">
      <div className="bg-background-blackpearl shadow-lg w-1/2">
        <div className="sticky top-0 bg-background-blackpearl w-full flex justify-end items-center pr-6 pt-6 pb-4 z-10">
          <button
            className="text-white hover:text-gray-300"
            onClick={() => setIsOpen(false)}
          >
            <Icons type="close" className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-col px-12 pb-12">
          <div className="flex flex-col text-xs font-medium overflow-y-auto h-[360px] scrollbar-transparent gap-y-4">
            <p className="text-2xl font-light uppercase text-white">
              Privacy Policy
            </p>
            {PrivacyContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
